import {
  filterFixedProducts,
  filterPromotionProducts,
  filterUsageProducts,
  isNotPromotionProduct,
  unsetProductDatesProcessor,
} from '@vy/services';
import { BILLING_INTERVALS, ContractType, Plan, PLAN_STATUSES } from '@vy/types';
import { isNotNullOrUndefined } from '@vy/utils';
import type { DeepPartial } from 'react-hook-form';
import type { PlanCreationStep } from '../../../../contexts/create-plan-context/create-plan-context-types/plan-creation-step';
import { PLAN_CREATION_STEPS } from '../../../../contexts/create-plan-context/create-plan-context-types/plan-creation-step';
import { getProRataSetting } from '../../../../services/get-pro-ration-setting-value';
import { processRateMultiplierPlanProducts } from '../../../../services/rate-multiplier-product-processor';
import { PRO_RATA_SETTINGS } from '../create-plan-page-components/CreatePlanForm/create-plan-form-types/create-plan-form-sub-types';
import type { PlanInputForm } from '../create-plan-page-components/CreatePlanForm/create-plan-form-types/plan-input-form';

export type StepsCtaLabels = Record<PlanCreationStep, React.ReactNode>;

export const DEFAULT_PLAN_DURATION = 12;

export const ZERO_DURATION_MONTHS = 0;

export const CREATE_PLAN_DEFAULT_VALUES: DeepPartial<PlanInputForm> = {
  status: PLAN_STATUSES.Active,
  billingData: {
    billingInterval: BILLING_INTERVALS.Monthly,
    duration: DEFAULT_PLAN_DURATION,
  },
  fixedProducts: [],
  usageProducts: [],
};

type GetCreatePlanDefaultValuesParams = {
  plan?: Plan;
  customerId?: string;
  startDate?: Date;
  signatureDate?: Date;
  contractType?: ContractType;
  shouldUnsetProductDates?: boolean;
};

export function getCreatePlanDefaultValues(
  params?: GetCreatePlanDefaultValuesParams,
): DeepPartial<PlanInputForm> {
  const { plan, customerId, startDate, signatureDate, contractType, shouldUnsetProductDates } = params ?? {};
  const defaultPlan = isNotNullOrUndefined(plan) ? getPlanDefaultValues(plan, shouldUnsetProductDates) : {};

  return {
    status: PLAN_STATUSES.Active,
    billingData: {
      billingInterval: BILLING_INTERVALS.Monthly,
      duration: DEFAULT_PLAN_DURATION,
      autoRenewal: true,
    },
    proRataSetting: PRO_RATA_SETTINGS.PRO_RATA_AND_CALENDAR_ALIGNED,
    fixedProducts: [],
    usageProducts: [],
    ...defaultPlan,
    customerId,
    startDate,
    signatureDate,
    contractType,
  };
}

function getPlanDefaultValues(plan: Plan, shouldUnsetProductDates = true) {
  const { products: basePlanProducts, ...planWithoutProducts } = plan;

  const rateAdjustedProducts = processRateMultiplierPlanProducts(basePlanProducts);
  const productsWithoutDates = unsetProductDatesProcessor(rateAdjustedProducts);
  const modifiedProducts = shouldUnsetProductDates ? productsWithoutDates : rateAdjustedProducts;

  const fixedProductsWithPromotions = filterFixedProducts(modifiedProducts);
  const fixedProducts = fixedProductsWithPromotions.filter(isNotPromotionProduct);
  const promotionProducts = filterPromotionProducts(fixedProductsWithPromotions);
  const usageProducts = filterUsageProducts(modifiedProducts);

  const usageProductsWithOriginalCommitments = usageProducts.map((product) => ({
    ...product,
    _originalCommitmentAmount: product.commitment?.amount,
    ...(product.commitment && {
      commitment: {
        ...product.commitment,
        autoRefillEnabled: isNotNullOrUndefined(product.commitment.replenishmentInterval),
      },
    }),
  }));

  return {
    ...planWithoutProducts,
    proRataSetting: getProRataSetting(plan),
    fixedProducts,
    usageProducts: usageProductsWithOriginalCommitments,
    promotionProducts,
  };
}

export const PLAN_CREATION_STEPS_CTA_LABELS: StepsCtaLabels = {
  [PLAN_CREATION_STEPS.settings]: 'Continue',
  [PLAN_CREATION_STEPS.pricingAndProducts]: 'Save Plan',
};

export const CONTRACT_UPDATE_STEPS_CTA_LABELS: StepsCtaLabels = {
  [PLAN_CREATION_STEPS.settings]: 'Continue',
  [PLAN_CREATION_STEPS.pricingAndProducts]: 'Save Contract',
};

export const CONTRACT_REVISE_STEPS_CTA_LABELS: StepsCtaLabels = {
  [PLAN_CREATION_STEPS.settings]: 'Continue',
  [PLAN_CREATION_STEPS.pricingAndProducts]: 'Save Contract',
};
